import React, { useState } from 'react';
import { Mail, CheckCircle, AlertCircle } from 'lucide-react';

const NewsletterForm = () => {
  const [email, setEmail] = useState('');
  const [gdprConsent, setGdprConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!gdprConsent) {
      setError('Devi accettare l\'informativa sulla privacy per iscriverti.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          gdpr_consent: gdprConsent,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Errore durante l\'iscrizione');
      }

      setSuccess(true);
      setEmail('');
      setGdprConsent(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-green-800 mb-2">Grazie per l'iscrizione!</h3>
        <p className="text-green-700">
          Ti abbiamo inviato un'email di conferma. Clicca il link nell'email per completare l'iscrizione.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl p-8 text-white">
      <div className="flex items-center justify-center mb-4">
        <Mail className="w-10 h-10 mr-3" />
        <h3 className="text-2xl font-bold">Iscriviti alla Newsletter</h3>
      </div>
      
      <p className="text-blue-100 text-center mb-6">
        Ricevi in anteprima le novità su eventi, tour esclusivi e offerte speciali!
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="La tua email"
            required
            className="w-full px-4 py-3 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-300"
            data-testid="newsletter-email-input"
          />
        </div>

        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            id="gdpr-consent"
            checked={gdprConsent}
            onChange={(e) => setGdprConsent(e.target.checked)}
            className="mt-1 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            data-testid="newsletter-gdpr-checkbox"
          />
          <label htmlFor="gdpr-consent" className="text-sm text-blue-100">
            Acconsento al trattamento dei miei dati personali per ricevere la newsletter di Offroad Italia, 
            in conformità con il <a href="/privacy" className="underline hover:text-white">Regolamento GDPR (UE) 2016/679</a>. 
            Posso revocare il consenso in qualsiasi momento cliccando il link di disiscrizione presente in ogni email.
          </label>
        </div>

        {error && (
          <div className="flex items-center bg-red-100 text-red-700 px-4 py-3 rounded-lg">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-white text-blue-600 font-bold py-3 px-6 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="newsletter-submit-btn"
        >
          {loading ? 'Iscrizione in corso...' : 'ISCRIVITI ALLA NEWSLETTER'}
        </button>
      </form>

      <p className="text-xs text-blue-200 text-center mt-4">
        I tuoi dati sono al sicuro. Non li condivideremo mai con terze parti.
      </p>
    </div>
  );
};

export default NewsletterForm;
